/* Waggle Enrich — product.js
   Variant chips (radio groups) → price roll, URL ?variant=, hidden id, availability ("Sold out"),
   gallery follow. Gallery progress rail (desktop), counter and dots (mobile), zoom dialog with FLIP.
   Add to basket state machine (idle → adding → added → drawer, arc hand-off, inline errors),
   sticky mobile buy bar, and "You may also like" through the Recommendations API. */

const W = window.Waggle;
const doc = document;

const initProduct = (section) => {
  const dataNode = section.querySelector('[data-product-json]');
  if (!dataNode) return;
  const product = JSON.parse(dataNode.textContent);
  const form = section.querySelector('[data-product-form]');
  const idInput = form && form.querySelector('[data-variant-id]');
  const picker = section.querySelector('[data-variant-picker]');
  const atc = section.querySelector('[data-atc]');
  const atcLabel = section.querySelector('[data-atc-label]');
  const errorBox = section.querySelector('[data-atc-error]');
  const priceWrap = section.querySelector('[data-product-price]');
  const buyBar = section.querySelector('[data-buy-bar]');
  const paymentWrap = form?.querySelector('.shopify-payment-button');
  const barPrice = section.querySelector('[data-buy-bar-price]');
  const barAdd = section.querySelector('[data-buy-bar-add]');
  const sectionId = section.dataset.sectionId;
  const quantityInput = form?.querySelector('input[name="quantity"]');
  if (!form || !idInput || !atc) return;

  /* Without JS a <select name="id"> carries the variant. With JS the hidden input does. */
  const noJsSelect = form.querySelector('[data-no-js-select]');
  if (noJsSelect) noJsSelect.remove();
  idInput.disabled = false;

  let current = product.variants.find((variant) => String(variant.id) === idInput.value) || product.variants[0];
  let state = 'idle';
  if (paymentWrap) paymentWrap.hidden = !current?.available;

  const labelFor = (variant) => {
    if (!variant) return W.strings.unavailable;
    if (!variant.available) return W.strings.soldOut;
    return W.strings.addToBasketPrice.replace('[price]', W.money(variant.price));
  };

  const swapLabel = (text) => {
    if (!atcLabel || atcLabel.textContent.trim() === text) return;
    const out = W.animate(atcLabel, [{ opacity: 1 }, { opacity: 0 }], { duration: 90, easing: W.ease.in, fill: 'forwards' });
    W.finished(out).then(() => {
      atcLabel.textContent = text;
      atcLabel.getAnimations().forEach((animation) => animation.cancel());
      W.animate(atcLabel, [{ opacity: 0 }, { opacity: 1 }], { duration: 140, easing: W.ease.out });
    });
  };

  const setState = (next) => {
    state = next;
    atc.classList.toggle('is-adding', next === 'adding');
    atc.classList.toggle('is-added', next === 'added');
    if (next === 'adding') {
      atc.setAttribute('aria-disabled', 'true');
      atc.setAttribute('aria-busy', 'true');
      swapLabel(W.strings.adding);
    } else if (next === 'added') {
      atc.removeAttribute('aria-busy');
      swapLabel(W.strings.added);
    } else {
      atc.removeAttribute('aria-busy');
      atc.removeAttribute('aria-disabled');
      swapLabel(labelFor(current));
    }
  };

  /* ---------- Price ---------- */
  const renderPrice = (variant) => {
    if (!priceWrap || !variant) return;
    const price = priceWrap.querySelector('.price');
    const currentEl = priceWrap.querySelector('[data-price-current]');
    const onSale = variant.compare_at_price > variant.price;
    if (price) price.classList.toggle('price--sale', onSale);
    W.rollText(currentEl, W.money(variant.price));
    W.qsa('[data-price-compare], .badge, .visually-hidden', price).forEach((node) => node.remove());
    if (onSale && price) {
      const saleLabel = doc.createElement('span');
      saleLabel.className = 'visually-hidden';
      saleLabel.textContent = (W.strings.salePrice || 'Sale price');
      price.prepend(saleLabel);
      const compare = doc.createElement('s');
      compare.className = 'price__compare';
      compare.setAttribute('data-price-compare', '');
      compare.textContent = W.money(variant.compare_at_price);
      price.append(compare);
    }
    if (barPrice) barPrice.textContent = W.money(variant.price);
  };

  /* ---------- Variant picker ---------- */
  const optionCount = product.variants[0] ? product.variants[0].options.length : 0;
  const selection = () => {
    const values = product.variants[0] ? product.variants[0].options.slice() : [];
    for (let index = 0; index < optionCount; index += 1) {
      const fieldset = picker && picker.querySelector(`[data-option-index="${index}"]`);
      const checked = fieldset && fieldset.querySelector('input:checked');
      if (checked) values[index] = checked.value;
    }
    return values;
  };
  const findVariant = (values) =>
    product.variants.find((variant) => variant.options.every((value, index) => value === values[index]));

  const updateAvailability = (values) => {
    if (!picker) return;
    W.qsa('[data-option-index]', picker).forEach((fieldset) => {
      const index = Number(fieldset.dataset.optionIndex);
      W.qsa('input[type="radio"]', fieldset).forEach((input) => {
        const candidate = values.slice();
        candidate[index] = input.value;
        const match = findVariant(candidate);
        const available = Boolean(match && match.available);
        const label = fieldset.querySelector(`label[for="${input.id}"]`);
        if (!label) return;
        label.classList.toggle('is-unavailable', !available);
        const statusNode = label.querySelector('.chip__status');
        if (statusNode) statusNode.textContent = available ? '' : `, ${match ? W.strings.soldOut : W.strings.unavailable}`;
      });
    });
  };

  const followMedia = (variant) => {
    if (!variant || !variant.media_id) return;
    const item = doc.getElementById(`Media-${sectionId}-${variant.media_id}`);
    if (!item) return;
    const list = item.parentElement;
    const behavior = W.reducedMotion() ? 'auto' : 'smooth';
    if (window.matchMedia('(min-width: 990px)').matches) {
      const top = item.getBoundingClientRect().top + window.scrollY - 120;
      if (W.lenis) W.lenis.scrollTo(top, { immediate: W.reducedMotion() });
      else window.scrollTo({ top, behavior });
    } else {
      list.scrollTo({ left: item.offsetLeft - list.offsetLeft, behavior });
    }
  };

  const update = () => {
    const values = selection();
    const variant = findVariant(values);
    current = variant || null;
    updateAvailability(values);
    if (!variant) {
      idInput.disabled = true;
      atc.disabled = true;
      if (barAdd) barAdd.disabled = true;
      if (paymentWrap) paymentWrap.hidden = true;
      swapLabel(W.strings.unavailable);
      W.rollText(priceWrap?.querySelector('[data-price-current]'), W.strings.unavailable);
      if (barPrice) barPrice.textContent = W.strings.unavailable;
      return;
    }
    idInput.disabled = false;
    const idChanged = idInput.value !== String(variant.id);
    idInput.value = variant.id;
    if (idChanged) idInput.dispatchEvent(new Event('change', { bubbles: true }));
    if (quantityInput) {
      const rule = variant.quantity_rule || {};
      const min = Number(rule.min) || 1;
      const increment = Number(rule.increment) || 1;
      quantityInput.min = String(min);
      quantityInput.step = String(increment);
      if (rule.max) quantityInput.max = String(rule.max);
      else quantityInput.removeAttribute('max');
      const chosen = Number(quantityInput.value);
      if (chosen < min || (rule.max && chosen > Number(rule.max)) || (chosen - min) % increment !== 0) quantityInput.value = String(min);
      W.syncSteppers?.(form);
    }
    atc.disabled = !variant.available;
    if (barAdd) barAdd.disabled = !variant.available;
    if (paymentWrap) paymentWrap.hidden = !variant.available;
    if (state === 'idle') swapLabel(labelFor(variant));
    renderPrice(variant);
    followMedia(variant);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState(window.history.state, '', url.toString());
  };

  if (picker) {
    picker.addEventListener('change', update);
    updateAvailability(selection());
  }

  /* ---------- Add to basket ---------- */
  form.addEventListener('submit', async (event) => {
    if (!event.submitter?.matches('[data-atc], [data-buy-bar-add]') || !W.cart) return;
    event.preventDefault();
    if (state !== 'idle' || !current || !current.available) return;
    if (quantityInput && !quantityInput.checkValidity()) {
      quantityInput.reportValidity();
      return;
    }
    if (errorBox) errorBox.textContent = '';
    setState('adding');
    try {
      const result = await W.cart.add(new FormData(form));
      if (!result.fresh) {
        window.location.assign(W.routes.cart);
        return;
      }
      if (W.settings.cartType !== 'drawer' || !doc.getElementById('CartDrawer')) {
        window.location.assign(W.routes.cart);
        return;
      }
      W.cart.render(result.sections);
      setState('added');
      const from = event.submitter && event.submitter.closest('[data-buy-bar]') ? event.submitter : atc;
      await Promise.all([W.motion ? W.motion.arc(from, product.image) : null, W.wait(500)]);
      const bubble = doc.querySelector('#CartCount [data-cart-count]');
      if (bubble) W.bump(bubble);
      W.overlays.open('CartDrawer', atc);
      const count = result.count;
      if (count !== null && count !== undefined) {
        W.announce(count === 1 ? W.strings.liveAddedOne : W.strings.liveAddedOther.replace('[count]', count));
      }
      setTimeout(() => setState('idle'), 900);
    } catch (error) {
      setState('idle');
      if (errorBox) errorBox.textContent = error.message || W.strings.error;
      W.announce(error.message || W.strings.error);
    }
  });

  /* ---------- Gallery: rail, counter, dots ---------- */
  const list = section.querySelector('[data-gallery-list]');
  const items = W.qsa('.gallery__item', section);
  const railItems = W.qsa('.gallery__rail-item', section);
  const dots = W.qsa('.gallery__dot', section);
  const counter = section.querySelector('[data-gallery-current]');
  const setActive = (index) => {
    railItems.forEach((item, i) => item.classList.toggle('is-active', i === index));
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    if (counter) counter.textContent = String(index + 1);
  };
  if (items.length > 1 && 'IntersectionObserver' in window) {
    const ratios = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => ratios.set(entry.target, entry.intersectionRatio));
        let best = 0;
        let bestRatio = -1;
        items.forEach((item, index) => {
          const ratio = ratios.get(item) || 0;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            best = index;
          }
        });
        setActive(best);
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    items.forEach((item) => observer.observe(item));
    if (list && 'onscrollend' in window) {
      list.addEventListener('scrollend', () => {
        const index = Math.round(list.scrollLeft / Math.max(1, items[0].offsetWidth));
        setActive(Math.min(items.length - 1, Math.max(0, index)));
      });
    }
  }

  /* ---------- Zoom dialog with FLIP ---------- */
  const zoom = section.querySelector('[data-zoom]');
  if (zoom) {
    const sources = JSON.parse(zoom.querySelector('[data-zoom-sources]').textContent || '[]');
    const zoomImg = zoom.querySelector('[data-zoom-img]');
    const zoomCount = zoom.querySelector('[data-zoom-count]');
    const toggle = zoom.querySelector('[data-zoom-toggle]');
    const stage = zoom.querySelector('[data-zoom-stage]');
    const buttons = W.qsa('[data-zoom-open]', section);
    let index = 0;
    const show = (next) => {
      if (!sources.length || !zoomImg) return;
      index = (next + sources.length) % sources.length;
      zoomImg.src = sources[index].src;
      zoomImg.alt = sources[index].alt || '';
      if (sources[index].w) zoomImg.width = sources[index].w;
      if (sources[index].h) zoomImg.height = sources[index].h;
      zoomCount.textContent = `${index + 1} / ${sources.length}`;
      zoom.classList.remove('is-zoomed');
      if (toggle) toggle.setAttribute('aria-pressed', 'false');
    };
    const flip = async (button) => {
      const fromImg = button.querySelector('img');
      if (!fromImg || W.reducedMotion()) return;
      await (zoomImg.decode ? zoomImg.decode().catch(() => {}) : null);
      const from = fromImg.getBoundingClientRect();
      const to = zoomImg.getBoundingClientRect();
      if (!to.width || !to.height) return;
      const dx = from.left + from.width / 2 - (to.left + to.width / 2);
      const dy = from.top + from.height / 2 - (to.top + to.height / 2);
      const scale = Math.min(from.width / to.width, from.height / to.height);
      zoomImg.animate(
        [
          { transform: `translate(${dx}px, ${dy}px) scale(${scale})` },
          { transform: 'translate(0, 0) scale(1)' },
        ],
        { duration: 520, easing: W.ease.inOut }
      );
      zoom.animate([{ backgroundColor: 'rgba(251, 248, 243, 0)' }, { backgroundColor: 'rgba(251, 248, 243, 1)' }], {
        duration: 520,
        easing: W.ease.standard,
      });
    };
    section.addEventListener('click', (event) => {
      const button = event.target.closest('[data-zoom-open]');
      if (!button) return;
      event.preventDefault();
      show(Math.max(0, buttons.indexOf(button)));
      W.overlays.open(zoom.id, button);
      flip(button);
    });
    zoom.querySelector('[data-zoom-prev]')?.addEventListener('click', () => show(index - 1));
    zoom.querySelector('[data-zoom-next]')?.addEventListener('click', () => show(index + 1));
    const setZoom = (on, event) => {
      zoom.classList.toggle('is-zoomed', on);
      if (toggle) toggle.setAttribute('aria-pressed', String(on));
      if (on && stage) {
        const rect = stage.getBoundingClientRect();
        const px = event ? (event.clientX - rect.left) / rect.width : 0.5;
        const py = event ? (event.clientY - rect.top) / rect.height : 0.5;
        requestAnimationFrame(() => {
          stage.scrollLeft = (stage.scrollWidth - stage.clientWidth) * px;
          stage.scrollTop = (stage.scrollHeight - stage.clientHeight) * py;
        });
      }
    };
    toggle?.addEventListener('click', () => setZoom(!zoom.classList.contains('is-zoomed')));
    stage?.addEventListener('click', (event) => {
      if (event.target === zoomImg) setZoom(!zoom.classList.contains('is-zoomed'), event);
    });
    zoom.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') show(index + 1);
      if (event.key === 'ArrowLeft') show(index - 1);
    });
  }

  /* ---------- Sticky mobile buy bar ---------- */
  if (buyBar && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(([entry]) => {
      const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      buyBar.classList.toggle('is-visible', visible);
      buyBar.inert = !visible;
    });
    observer.observe(atc);
  }
};

W.qsa('[data-product]').forEach(initProduct);

/* ---------- Recommendations (intent=related), loaded when near the viewport ---------- */
W.qsa('[data-recommendations]').forEach((container) => {
  const url = container.dataset.url;
  if (!url || !('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver(
    async ([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      try {
        const response = await fetch(url);
        const html = await response.text();
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
        if (!fresh || !fresh.innerHTML.trim()) return;
        /* JS guard: drop any card without a photo. */
        W.qsa('.grid__item', fresh).forEach((item) => {
          if (!item.querySelector('.plate img')) item.remove();
        });
        if (!fresh.querySelector('.grid__item')) return;
        container.innerHTML = fresh.innerHTML;
        if (W.motion) W.motion.observe(container);
      } catch (error) {
        /* Recommendations are optional; the page is complete without them. */
      }
    },
    { rootMargin: '0px 0px 400px 0px' }
  );
  observer.observe(container);
});
